/**
 * Lightweight markdown renderer (no external deps).
 * Supports: headings, bold, lists, code, blockquote, paragraphs.
 */
const escapeHtml = (s) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const renderInline = (text) => {
  let t = escapeHtml(text);
  t = t.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  t = t.replace(/\*(.+?)\*/g, "<em>$1</em>");
  t = t.replace(/`([^`]+)`/g, "<code>$1</code>");
  t = t.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener" class="text-[#8a5e10] underline">$1</a>');
  return t;
};

const renderMarkdown = (md) => {
  if (!md) return "";
  const lines = md.split("\n");
  const out = [];
  let inList = false;
  let listType = "ul";
  let inCode = false;
  let codeBuf = [];

  const flushList = () => {
    if (inList) {
      out.push(`</${listType}>`);
      inList = false;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.startsWith("```")) {
      if (inCode) {
        out.push(`<pre class="bg-[#F0F0F0] rounded-lg p-3 font-mono text-xs overflow-x-auto my-2"><code>${codeBuf.map(escapeHtml).join("\n")}</code></pre>`);
        codeBuf = [];
        inCode = false;
      } else {
        flushList();
        inCode = true;
      }
      continue;
    }
    if (inCode) {
      codeBuf.push(line);
      continue;
    }

    if (/^###\s/.test(line)) {
      flushList();
      out.push(`<h3>${renderInline(line.replace(/^###\s/, ""))}</h3>`);
    } else if (/^##\s/.test(line)) {
      flushList();
      out.push(`<h2>${renderInline(line.replace(/^##\s/, ""))}</h2>`);
    } else if (/^#\s/.test(line)) {
      flushList();
      out.push(`<h1>${renderInline(line.replace(/^#\s/, ""))}</h1>`);
    } else if (/^>\s/.test(line)) {
      flushList();
      out.push(`<blockquote>${renderInline(line.replace(/^>\s/, ""))}</blockquote>`);
    } else if (/^[-*]\s/.test(line)) {
      if (!inList || listType !== "ul") {
        flushList();
        out.push("<ul>");
        inList = true;
        listType = "ul";
      }
      out.push(`<li>${renderInline(line.replace(/^[-*]\s/, ""))}</li>`);
    } else if (/^\d+\.\s/.test(line)) {
      if (!inList || listType !== "ol") {
        flushList();
        out.push("<ol>");
        inList = true;
        listType = "ol";
      }
      out.push(`<li>${renderInline(line.replace(/^\d+\.\s/, ""))}</li>`);
    } else if (line.trim() === "") {
      flushList();
      out.push("");
    } else {
      flushList();
      out.push(`<p>${renderInline(line)}</p>`);
    }
  }
  flushList();
  if (inCode) out.push(`<pre><code>${codeBuf.map(escapeHtml).join("\n")}</code></pre>`);
  return out.join("\n");
};

const Markdown = ({ content, className = "" }) => (
  <div
    className={`markdown-content ${className}`}
    dangerouslySetInnerHTML={{ __html: renderMarkdown(content || "") }}
  />
);

export default Markdown;
